// Handing the current view to somebody else.
//
// Two mechanisms, and the order between them is the whole content of this
// file. `navigator.share` opens the platform's own sheet — the one with the
// reader's actual messaging apps in it — and is the right answer on a phone,
// which is where somebody standing at a bus stop is. It does not exist on most
// desktop browsers, where copying the link is what a person wanted anyway.
//
// The trap is the middle case. `navigator.share` rejects with an AbortError
// when the reader opens the sheet and then closes it, and the obvious
// try/catch treats that identically to "sharing is broken" and copies the link
// instead. So dismissing the share sheet would silently put the URL on the
// clipboard, overwriting whatever was there — a small theft, performed by a
// gesture that means "no". Cancelling has to mean cancelled.
//
// The DOM and the two browser APIs arrive as parameters so this is testable
// without either.

export type ShareOutcome =
  /** The platform sheet took it. */
  | 'shared'
  /** No sheet, so the link went to the clipboard. */
  | 'copied'
  /** The reader opened the sheet and backed out. Do nothing. */
  | 'dismissed'
  /** Neither mechanism was available or permitted. */
  | 'failed';

export interface ShareDeps {
  /** navigator.share, or undefined where it does not exist. */
  share?: (data: { title: string; url: string }) => Promise<void>;
  /** navigator.clipboard.writeText, or undefined. */
  copy?: (text: string) => Promise<void>;
}

export async function shareView(
  url: string,
  title: string,
  deps: ShareDeps,
): Promise<ShareOutcome> {
  if (deps.share) {
    try {
      await deps.share({ title, url });
      return 'shared';
    } catch (err) {
      // The reader closed the sheet. Their answer was no; respect it rather
      // than reaching for the clipboard on their behalf.
      if ((err as { name?: string } | null)?.name === 'AbortError') return 'dismissed';
      // Anything else — a browser that advertises share and refuses the data,
      // a permissions policy — is a real failure of that mechanism, so fall
      // through and try the other one.
    }
  }

  if (deps.copy) {
    try {
      await deps.copy(url);
      return 'copied';
    } catch {
      // Refused: an insecure context, or a permission denied.
    }
  }

  return 'failed';
}

/** What the button should say, once it knows what happened. */
export function shareMessage(outcome: ShareOutcome): string | null {
  switch (outcome) {
    case 'copied': return 'Link copied';
    // The platform sheet already told them, and the button confirming it a
    // second time is noise on top of an animation they just watched.
    case 'shared': return null;
    case 'dismissed': return null;
    case 'failed': return 'Press Ctrl+C to copy the address';
  }
}
